import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
    ECustomerActions,
    GetCustomer,
    GetCustomerSuccess,
    GetCustomerFailure,
    UpdateCustomerSuccess,
    UpdateCustomerFailure,
    DeleteCustomer,
    DeleteCustomerSuccess,
    UpdatePassword,
    UpdatePasswordSuccess,
    UpdatePasswordFailure,
    UpdatePersonnelInformationCustomer,
    UpdateBillingAddress
} from '../actions/customer.actions';
import { CustomerService } from '../../services/customer.service';
import { CustomerInterface } from '../../models/customer.interface';
import { CustomerStorageService } from '../../services/customer-storage.service';
import { ConstsHelper } from '../../../../helpers/consts.helper';
import { Store } from '@ngrx/store';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractEffects } from '../../../shared/abstract.effects';
import { ELoginActions } from '../../../authentication/store/actions/login.actions';
import { UpdatePasswordInterface } from '../../models/update-password.interface';
import { SaveCustomerMission } from '../../../shared/store/actions/customer-mission.actions';
import { UpdateSelectedProcedure } from '../../../shared/store/actions/selected-procedure.actions';
import { MfaInterface } from '../../../shared/models/mfa.interface';
import { UpdateMfaVisibility } from '../../../shared/store/actions/mfa.actions';

@Injectable()
export class CustomerEffects extends AbstractEffects {

    customer: CustomerInterface = null;
    mfa: MfaInterface|null = null;
    updateCustomerPassword: UpdatePasswordInterface = null;

    constructor(
        private router: Router,
        private actions: Actions,
        private localStorageService: CustomerStorageService,
        private customerService: CustomerService,
        public override store: Store<any>
    ) {
        super(store);
    }

    getCustomer = createEffect(() => this.actions.pipe(
        ofType<GetCustomer>(ECustomerActions.GetCustomer), // Type of action, listen to the event dispatched
        switchMap(() => this.customerService.get()), // Call service to get customer
        switchMap((response: CustomerInterface) => of(new GetCustomerSuccess(response))), // Dispatch success event that will be catched up by reducer
        tap(response => { this.localStorageService.save(response.payload); }), // Save customer data in local storage
        catchError((error: HttpErrorResponse) => {
            if ([401, 403].indexOf(error.status) !== -1) {
                this.ResendRequestWithNewToken(new GetCustomer());
            } else {
                this.DisplayMessage('error_with_retry', 400, new GetCustomerFailure());
            }
            return throwError(error);
        })
    ));

    updateBillingAddress = createEffect(() => this.actions.pipe(
        ofType<UpdateBillingAddress>(ECustomerActions.UpdateBillingAddress), // Type of action, listen to the event dispatched
        tap(params => {
            this.customer = params.payload;
            this.mfa = params.mfa;
        }), // Get passed params
        switchMap(() => this.customerService.updateAddress(this.customer, this.mfa)), // Call service to update data
        switchMap(() => of(new UpdateCustomerSuccess())), // Dispatch success event that will be catched up by reducer
        tap( () => {
            if (this.mfa) {
                this.mfa.visibility = false;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            }
            this.store.dispatch(
                new SaveCustomerMission(
                    {
                        step: 1,
                        street: this.customer.street,
                        addressComplement: this.customer.addressComplement,
                        zipCode: this.customer.zipCode,
                        city: this.customer.city,
                        placeName: this.customer.placeName,
                    },
                    ConstsHelper.PROCEDURE_BILLING_ADDRESS_CHANGE,
                    'account'
                )
            );
            this.store.dispatch(new GetCustomer());
            this.DisplayMessage(ConstsHelper.PROCEDURE_BILLING_ADDRESS_CHANGE, 200, new GetCustomer());
            this.router.navigate(['/compte/adresses']);
        }),
        catchError((error: HttpErrorResponse) => {
            if ([401, 403].indexOf(error.status) !== -1) {
                this.ResendRequestWithNewToken(new UpdateBillingAddress(this.customer, this.mfa));
            } else if (this.mfa && error.error.error_message && error.error.error_message.includes('invalid_code')) {
                this.mfa.errorMessage = ConstsHelper.ERROR_MESSAGE_MFA_INVALID_CODE;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            } else if (this.mfa && error.error.error_message && error.error.error_message.includes('blocked')) {
                this.mfa.errorMessage = ConstsHelper.ERROR_MESSAGE_MFA_BLOCKED;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            } else {
                this.DisplayMessage('error_with_retry', 400, new UpdateCustomerFailure());
            }
            return throwError(error);
        })
    ));

    updatePersonnelInformationCustomer = createEffect(() => this.actions.pipe(
        ofType<UpdatePersonnelInformationCustomer>(ECustomerActions.UpdatePersonnelInformationCustomer), // Type of action, listen to the event dispatched
        tap(params => {
            this.customer = params.payload;
            this.mfa = params.mfa;
        }),
        switchMap(() => this.customerService.updatePersonnelInformation(
            this.customer.email,
            this.customer.phoneNumber,
            this.customer.routingAddress,
            this.customer.codeService,
            this.customer.numeroCommande,
            this.customer.buyerReference,
            this.customer.password,
            this.mfa
        )), // Call service to update data
        switchMap(() => of(new UpdateCustomerSuccess())), // Dispatch success event that will be catched up by reducer
        tap( () => {
            if (this.mfa) {
                this.mfa.visibility = false;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            }
            this.store.dispatch(new GetCustomer());
            this.DisplayMessage(ConstsHelper.PROCEDURE_CUSTOMER_INFORMATION_CHANGE, 200, new GetCustomer());
        }),
        catchError((error: HttpErrorResponse) => {
            if ([401, 403].indexOf(error.status) !== -1) {
                this.ResendRequestWithNewToken(new UpdatePersonnelInformationCustomer(this.customer, this.mfa));
            } else if (this.mfa && error.error.error_message && error.error.error_message.includes('invalid_code')) {
                this.mfa.errorMessage = ConstsHelper.ERROR_MESSAGE_MFA_INVALID_CODE;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            } else if (this.mfa && error.error.error_message && error.error.error_message.includes('blocked')) {
                this.mfa.errorMessage = ConstsHelper.ERROR_MESSAGE_MFA_BLOCKED;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            } else if (error.error.error_message && error.error.error_message.includes('email is used by another customer')) {
                this.DisplayMessage('email_already_used', 400, new UpdateCustomerFailure());
                this.mfa.visibility = false;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            } else {
                this.DisplayMessage('error_with_retry', 400, new UpdateCustomerFailure());
            }
            return throwError(error);
        })
    ));

    updatePassword = createEffect(() => this.actions.pipe(
        ofType<UpdatePassword>(ECustomerActions.UpdatePassword),
        tap(params => {
            this.updateCustomerPassword = params.payload;
            this.mfa = params.mfa;
        }),
        switchMap(() => this.customerService.updatePassword(this.updateCustomerPassword, this.mfa)),
        switchMap(() => of(new UpdatePasswordSuccess())),
        tap(() => {
            if (this.mfa) {
                this.mfa.visibility = false;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            }
            this.store.dispatch(
                new SaveCustomerMission(
                    {
                        step: 1,
                        message: 'Password updated',
                    },
                    ConstsHelper.PROCEDURE_CUSTOMER_PASSWORD_CHANGE,
                    'account'
                )
            );
            this.store.dispatch(new UpdateSelectedProcedure(''));
        }),
        catchError((error: HttpErrorResponse) => {
            if ([401, 403].indexOf(error.status) !== -1) {
                this.ResendRequestWithNewToken(new UpdatePassword(this.updateCustomerPassword, this.mfa));
            } else if (this.mfa && error.error.error_message && error.error.error_message.includes('invalid_code')) {
                this.mfa.errorMessage = ConstsHelper.ERROR_MESSAGE_MFA_INVALID_CODE;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            } else if (this.mfa && error.error.error_message && error.error.error_message.includes('blocked')) {
                this.mfa.errorMessage = ConstsHelper.ERROR_MESSAGE_MFA_BLOCKED;
                this.store.dispatch(new UpdateMfaVisibility(this.mfa));
            } else {
                this.DisplayMessage('error_with_retry', 400, new UpdatePasswordFailure());
            }
            return throwError(error);
        })
    ));

    deleteLocalCustomer = createEffect(() => this.actions.pipe(
        ofType<DeleteCustomer>(ELoginActions.DeleteToken),
        switchMap(() => of(new DeleteCustomerSuccess())),
        tap(() => {
            this.localStorageService.clear();
        })
    ));
}
